import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { createSmoothTone } from "@/lib/audioEngine";

const CAMELOT_OUTER = [
  { code: "1B",  key: "B",   freq: 484.90 },
  { code: "2B",  key: "F#",  freq: 363.27 },
  { code: "3B",  key: "Db",  freq: 272.14 },
  { code: "4B",  key: "Ab",  freq: 408.24 },
  { code: "5B",  key: "Eb",  freq: 306.00 },
  { code: "6B",  key: "Bb",  freq: 458.16 },
  { code: "7B",  key: "F",   freq: 343.30 },
  { code: "8B",  key: "C",   freq: 257.18 },
  { code: "9B",  key: "G",   freq: 385.55 },
  { code: "10B", key: "D",   freq: 288.33 },
  { code: "11B", key: "A",   freq: 432.00 },
  { code: "12B", key: "E",   freq: 324.00 },
];

const CAMELOT_INNER = [
  { code: "1A",  key: "Abm", freq: 408.24 },
  { code: "2A",  key: "Ebm", freq: 306.00 },
  { code: "3A",  key: "Bbm", freq: 458.16 },
  { code: "4A",  key: "Fm",  freq: 343.30 },
  { code: "5A",  key: "Cm",  freq: 257.18 },
  { code: "6A",  key: "Gm",  freq: 385.55 },
  { code: "7A",  key: "Dm",  freq: 288.33 },
  { code: "8A",  key: "Am",  freq: 432.00 },
  { code: "9A",  key: "Em",  freq: 324.00 },
  { code: "10A", key: "Bm",  freq: 484.90 },
  { code: "11A", key: "F#m", freq: 363.27 },
  { code: "12A", key: "Dbm", freq: 272.14 },
];

// Red spectrum — 12 shades from deep to bright
const OUTER_COLORS = [
  "#FF0000","#E60000","#CC0000","#FF1A1A","#B30000","#FF3333",
  "#990000","#FF4D4D","#800000","#FF1A1A","#CC0000","#E60000",
];
const INNER_COLORS = [
  "#990000","#800000","#660000","#8B0000","#4D0000","#730000",
  "#5C0000","#660000","#4D0000","#800000","#730000","#660000",
];

const cx = 300, cy = 300;
const outerR = 260, midR = 188, innerR = 118, centerR = 50;

const arcPath = (startA, endA, rO, rI) => {
  const s = (startA - 90) * Math.PI / 180;
  const e = (endA - 90) * Math.PI / 180;
  const x1 = cx + rO * Math.cos(s), y1 = cy + rO * Math.sin(s);
  const x2 = cx + rO * Math.cos(e), y2 = cy + rO * Math.sin(e);
  const x3 = cx + rI * Math.cos(e), y3 = cy + rI * Math.sin(e);
  const x4 = cx + rI * Math.cos(s), y4 = cy + rI * Math.sin(s);
  const large = (endA - startA) > 180 ? 1 : 0;
  return `M${x1},${y1} A${rO},${rO},0,${large},1,${x2},${y2} L${x3},${y3} A${rI},${rI},0,${large},0,${x4},${y4} Z`;
};

const midAngle = (s, e) => ((s + e) / 2 - 90) * Math.PI / 180;

export default function CamelotWheel() {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const toneRef = useRef(null);
  const fadeTimerRef = useRef(null);

  useEffect(() => () => {
    if (toneRef.current?.isAlive) toneRef.current.destroy();
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
  }, []);

  const play = useCallback((freq) => {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    if (!toneRef.current || !toneRef.current.isAlive) {
      toneRef.current = createSmoothTone({ freq, gain: 0.2 });
      toneRef.current.fadeIn(0.2);
    } else {
      toneRef.current.setFrequency(freq);
      toneRef.current.setGain(0.2);
    }
    // Auto fade-out after 3s
    fadeTimerRef.current = setTimeout(() => {
      if (toneRef.current?.isAlive) {
        toneRef.current.fadeOut();
        const ref = toneRef.current;
        setTimeout(() => { if (ref.isAlive) ref.destroy(); }, 250);
        toneRef.current = null;
      }
    }, 3000);
  }, []);

  const handleClick = (ring, i) => {
    const item = ring === "outer" ? CAMELOT_OUTER[i] : CAMELOT_INNER[i];
    setSelected({ ring, index: i });
    play(item.freq);
  };

  const step = 360 / 12;

  const getCompatible = () => {
    if (!selected) return [];
    const src = selected.ring === "outer" ? CAMELOT_OUTER : CAMELOT_INNER;
    const item = src[selected.index];
    const num = parseInt(item.code);
    const letter = item.code.includes("B") ? "B" : "A";
    return [
      `${num}${letter === "A" ? "B" : "A"}`,
      `${num === 12 ? 1 : num + 1}${letter}`,
      `${num === 1 ? 12 : num - 1}${letter}`,
      item.code,
    ];
  };

  const compatCodes = getCompatible();
  const isCompat = (code) => compatCodes.includes(code);
  const isSelected = (ring, i) => selected?.ring === ring && selected?.index === i;

  const selInfo = selected
    ? selected.ring === "outer" ? CAMELOT_OUTER[selected.index] : CAMELOT_INNER[selected.index]
    : null;

  return (
    <Card className="sg-frame border-[#FF0000]/20" data-testid="camelot-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-[#FF0000] text-sm tracking-widest font-mono" data-testid="codes-title">
          &gt; INTERACTIVE_MIRRORS_WHEEL
        </CardTitle>
        <p className="text-[10px] tracking-widest uppercase text-[#666]">
          Tap segments &middot; Adjacent keys mix smoothly &middot; 432Hz tuning &middot; Smooth frequency glide
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="w-full bg-black border border-[#FF0000]/15 p-3 flex justify-center"
          data-testid="camelot-wheel-container"
          style={{ filter: "drop-shadow(0 0 25px rgba(255,0,0,0.12))" }}
        >
          <svg viewBox="0 0 600 600" className="w-full h-auto" style={{ maxHeight: "480px" }}>
            <defs>
              {/* Glow filter for selected segments */}
              <filter id="glow-sel" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="glow-compat" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              {/* Radial gradient for center */}
              <radialGradient id="center-grad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FF0000" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#000" stopOpacity="1" />
              </radialGradient>
            </defs>

            {/* Ambient ring glow */}
            <circle cx={cx} cy={cy} r={outerR + 8} fill="none" stroke="#FF0000" strokeWidth="1" opacity="0.15" />
            <circle cx={cx} cy={cy} r={outerR + 16} fill="none" stroke="#FF0000" strokeWidth="0.5" opacity="0.08" />

            {/* Outer ring — 12 Major keys (B) */}
            {CAMELOT_OUTER.map((item, i) => {
              const s = i * step, e = (i + 1) * step;
              const sel = isSelected("outer", i);
              const hov = hovered?.ring === "outer" && hovered.index === i;
              const compat = isCompat(item.code) && !sel;
              const m = midAngle(s, e);
              const labelR = (outerR + midR) / 2;
              return (
                <g key={`o-${i}`}>
                  <path
                    d={arcPath(s, e, outerR, midR)}
                    fill={OUTER_COLORS[i]}
                    opacity={sel ? 1 : compat ? 0.75 : hov ? 0.6 : 0.3}
                    stroke={sel ? "#FF0000" : compat ? "#FF0000" : "#220000"}
                    strokeWidth={sel ? 2.5 : compat ? 1.5 : 0.5}
                    filter={sel ? "url(#glow-sel)" : compat ? "url(#glow-compat)" : undefined}
                    style={{ cursor: "pointer", transition: "opacity 0.2s, stroke-width 0.2s" }}
                    onMouseEnter={() => setHovered({ ring: "outer", index: i })}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => handleClick("outer", i)}
                    data-testid={`camelot-outer-${i}`}
                  />
                  <text x={cx + labelR * Math.cos(m)} y={cy + labelR * Math.sin(m) - 6}
                    textAnchor="middle" dominantBaseline="central"
                    fill={sel || compat ? "#fff" : "#FF0000"} fontSize="12" fontWeight="bold" fontFamily="monospace"
                    opacity={sel ? 1 : compat ? 0.9 : 0.6}
                    style={{ pointerEvents: "none" }}
                  >
                    {item.code}
                  </text>
                  <text x={cx + labelR * Math.cos(m)} y={cy + labelR * Math.sin(m) + 7}
                    textAnchor="middle" dominantBaseline="central"
                    fill={sel ? "#fff" : "#FF0000"} fontSize="9" fontFamily="monospace"
                    opacity={sel ? 0.9 : 0.4}
                    style={{ pointerEvents: "none" }}
                  >
                    {item.key}
                  </text>
                </g>
              );
            })}

            {/* Inner ring — 12 Minor keys (A) */}
            {CAMELOT_INNER.map((item, i) => {
              const s = i * step, e = (i + 1) * step;
              const sel = isSelected("inner", i);
              const hov = hovered?.ring === "inner" && hovered.index === i;
              const compat = isCompat(item.code) && !sel;
              const m = midAngle(s, e);
              const labelR = (midR + innerR) / 2;
              return (
                <g key={`i-${i}`}>
                  <path
                    d={arcPath(s, e, midR, innerR)}
                    fill={INNER_COLORS[i]}
                    opacity={sel ? 1 : compat ? 0.75 : hov ? 0.6 : 0.3}
                    stroke={sel ? "#FF0000" : compat ? "#FF0000" : "#1A0000"}
                    strokeWidth={sel ? 2.5 : compat ? 1.5 : 0.5}
                    filter={sel ? "url(#glow-sel)" : compat ? "url(#glow-compat)" : undefined}
                    style={{ cursor: "pointer", transition: "opacity 0.2s, stroke-width 0.2s" }}
                    onMouseEnter={() => setHovered({ ring: "inner", index: i })}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => handleClick("inner", i)}
                    data-testid={`camelot-inner-${i}`}
                  />
                  <text x={cx + labelR * Math.cos(m)} y={cy + labelR * Math.sin(m) - 5}
                    textAnchor="middle" dominantBaseline="central"
                    fill={sel || compat ? "#fff" : "#FF0000"} fontSize="10" fontWeight="bold" fontFamily="monospace"
                    opacity={sel ? 1 : compat ? 0.9 : 0.5}
                    style={{ pointerEvents: "none" }}
                  >
                    {item.code}
                  </text>
                  <text x={cx + labelR * Math.cos(m)} y={cy + labelR * Math.sin(m) + 6}
                    textAnchor="middle" dominantBaseline="central"
                    fill={sel ? "#fff" : "#FF0000"} fontSize="8" fontFamily="monospace"
                    opacity={sel ? 0.8 : 0.35}
                    style={{ pointerEvents: "none" }}
                  >
                    {item.key}
                  </text>
                </g>
              );
            })}

            {/* Center */}
            <circle cx={cx} cy={cy} r={centerR} fill="url(#center-grad)" stroke="#FF0000" strokeWidth="1.5" />
            <text x={cx} y={cy - 10} textAnchor="middle" fill="#FF0000" fontSize="13" fontWeight="bold" fontFamily="monospace">MIRRORS</text>
            <text x={cx} y={cy + 7} textAnchor="middle" fill="#FF0000" fontSize="10" fontFamily="monospace" opacity="0.6">432Hz</text>

            {/* Selected key frequency arc indicator */}
            {selInfo && (
              <circle cx={cx} cy={cy} r={centerR + 8} fill="none" stroke="#FF0000" strokeWidth="1"
                strokeDasharray="4,3" opacity="0.4">
                <animateTransform attributeName="transform" type="rotate"
                  from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="12s" repeatCount="indefinite" />
              </circle>
            )}
          </svg>
        </div>

        {/* Selected key info */}
        {selInfo && (
          <div className="bg-black border border-[#FF0000]/25 p-2.5 font-mono text-xs" data-testid="camelot-info">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" />
                <span className="text-[#FF0000] font-bold tracking-widest">{selInfo.code}</span>
                <span className="text-white/80">{selInfo.key}</span>
                <span className="text-[#FF0000]/40">—</span>
                <span className="text-white/70">{selInfo.freq} Hz</span>
              </div>
              <div className="text-[9px] text-[#666] tracking-widest">
                MIX: {compatCodes.filter(c => c !== selInfo.code).map(c => c).join(" · ")}
              </div>
            </div>
          </div>
        )}

        {/* Compact legend */}
        <div className="flex items-center justify-between text-[8px] font-mono tracking-widest text-[#555]">
          <span>OUTER = MAJOR (B) · INNER = MINOR (A)</span>
          <span>ADJACENT + CROSS = HARMONIC MIX</span>
        </div>
      </CardContent>
    </Card>
  );
}
